import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ActionBar from '../components/ActionBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Skeleton } from '../components/ui/skeleton';
import { BedDouble, Users, Maximize2, ArrowRight, CalendarDays, ChevronLeft, Edit2 } from 'lucide-react';
import axios from 'axios';
import { saveSearch } from '../lib/searchState';

export default function RoomsPage() {
  const { t, language, formatPrice, BACKEND_URL, getImageUrl } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkIn = searchParams.get('check_in');
  const checkOut = searchParams.get('check_out');
  const guests = searchParams.get('guests') || '2';
  const childrenParam = searchParams.get('children') || '';
  const childAges = childrenParam ? childrenParam.split(',').map(a => parseInt(a)).filter(a => !isNaN(a)) : [];
  const hasSearch = checkIn && checkOut;
  const searchSuffix = hasSearch
    ? `?check_in=${checkIn}&check_out=${checkOut}&guests=${guests}${childrenParam ? `&children=${childrenParam}` : ''}`
    : '';

  useEffect(() => {
    if (hasSearch) saveSearch({ checkIn, checkOut, guests: parseInt(guests), children: childAges });
  }, [checkIn, checkOut, guests, childrenParam]);

  const nights = hasSearch
    ? Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
    : 0;

  // Set page title
  useEffect(() => {
    document.title = language === 'tr'
      ? 'Odalar & Fiyatlar | Kastamonu Amorium Hotel | 4 Mevsim Doğa Tatili'
      : 'Rooms & Prices | Kastamonu Amorium Hotel | 4 Seasons Nature Holiday';
  }, [language]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (hasSearch) {
      params.set('available_only', 'true');
      params.set('check_in', checkIn);
      params.set('check_out', checkOut);
      params.set('guests', guests);
    }
    axios.get(`${BACKEND_URL}/api/rooms?${params.toString()}`)
      .then(res => { setRooms(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [checkIn, checkOut, guests, BACKEND_URL, hasSearch]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/discounts/active`)
      .then(res => setActiveDiscounts(res.data))
      .catch(() => {});
  }, [BACKEND_URL]);

  const discountFor = (roomId) => {
    const refStart = checkIn || new Date().toISOString().slice(0, 10);
    const refEnd = checkOut || refStart;
    let best = null;
    for (const d of activeDiscounts) {
      const applies = !d.room_ids || d.room_ids.length === 0 || d.room_ids.includes(roomId);
      if (applies && d.start_date <= refEnd && d.end_date >= refStart) {
        if (!best || d.percent > best.percent) best = d;
      }
    }
    return best;
  };

  const handleBook = (room) => {
    if (hasSearch) {
      navigate(`/booking?room_id=${room._id}&check_in=${checkIn}&check_out=${checkOut}&guests=${guests}${childrenParam ? `&children=${childrenParam}` : ''}`);
    } else {
      navigate(`/rooms/${room._id}${searchSuffix}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' }}>
      <ActionBar />
      <Navbar />

      {/* Date Summary Bar - shows dates passed from home page */}
      {hasSearch && (
        <div style={{ background: '#1F3A2E', padding: '12px 0' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.9)' }}>
                <CalendarDays size={15} style={{ color: '#B08D57' }} />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatDate(checkIn)} → {formatDate(checkOut)}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ background: '#B08D57', color: 'white', fontSize: '12px', fontWeight: 700, padding: '2px 10px', borderRadius: '20px' }}>
                  {nights} {language === 'tr' ? 'gece' : 'nights'}
                </span>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                  · {guests} {t('guest_count')}{childAges.length > 0 && ` + ${childAges.length} ${language === 'tr' ? 'çocuk' : 'children'}`}
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#B08D57', background: 'transparent', border: '1px solid rgba(176,141,87,0.4)', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}
            >
              <Edit2 size={12} />
              {language === 'tr' ? 'Tarihleri Değiştir' : 'Change Dates'}
            </button>
          </div>
        </div>
      )}

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>
        {/* Header */}
        {!hasSearch && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#6E7F5B', background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <ChevronLeft size={14} /> {t('home')}
            </button>
          </div>
        )}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 600, color: '#1F3A2E', marginBottom: '6px' }}>
            {hasSearch ? t('available_rooms') : (language === 'tr' ? 'Tüm Odalarımız' : 'All Our Rooms')}
          </h1>
          {!hasSearch && (
            <p style={{ fontSize: '14px', color: '#6E7F5B' }}>
              {language === 'tr' ? 'Ana sayfadan tarih seçerek müsait odaları filtreleyebilirsiniz.' : 'Select dates from the home page to filter available rooms.'}
            </p>
          )}
        </div>

        {/* Rooms Grid */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #D8D1C6', background: 'white' }}>
                <Skeleton style={{ height: '220px', width: '100%' }} />
                <div style={{ padding: '16px' }}>
                  <Skeleton style={{ height: '20px', width: '60%', marginBottom: '8px' }} />
                  <Skeleton style={{ height: '16px', width: '100%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : rooms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 0' }}>
            <BedDouble size={48} style={{ color: '#D8D1C6', margin: '0 auto 16px' }} />
            <p style={{ color: '#6E7F5B', fontSize: '16px', marginBottom: '16px' }}>{t('no_rooms')}</p>
            <button
              onClick={() => navigate('/')}
              style={{ background: '#1F3A2E', color: 'white', padding: '12px 28px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}
            >
              {language === 'tr' ? 'Tarihleri Değiştir' : 'Change Dates'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {rooms.map(room => (
              <RoomCard
                key={room._id}
                room={room}
                hasSearch={hasSearch}
                nights={nights}
                onBook={() => handleBook(room)}
                onDetail={() => navigate(`/rooms/${room._id}${searchSuffix}`)}
                t={t}
                language={language}
                formatPrice={formatPrice}
                checkIn={checkIn}
                checkOut={checkOut}
                guests={parseInt(guests)}
                childAges={childAges}
                discount={discountFor(room._id)}
                BACKEND_URL={BACKEND_URL}
              />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function RoomCard({ room, hasSearch, nights, onBook, onDetail, t, language, formatPrice, checkIn, checkOut, guests, childAges, discount, BACKEND_URL }) {
  const { getImageUrl } = useApp();
  const [priceInfo, setPriceInfo] = useState(null);
  const amenities = language === 'tr' ? room.amenities_tr : room.amenities_en;

  useEffect(() => {
    if (hasSearch && checkIn && checkOut) {
      // Clamp guests to this room's capacity (public search may request > this room's max).
      const g = Math.min(guests || 2, room.capacity || 2);
      axios.post(`${BACKEND_URL}/api/pricing/calculate`, {
        room_id: room._id, check_in: checkIn, check_out: checkOut, currency: 'TRY', guests: g,
        children: (childAges || []).slice(0, room.child_policy?.max_children ?? 2),
      }).then(res => setPriceInfo(res.data)).catch(() => {});
    }
  }, [room._id, room.capacity, hasSearch, checkIn, checkOut, guests, (childAges || []).join(','), BACKEND_URL]);

  return (
    <div
      data-testid="room-card"
      style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', border: '1px solid #EFE7DA', boxShadow: '0 2px 12px rgba(31,58,46,0.06)', transition: 'transform 0.2s, box-shadow 0.2s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 32px rgba(31,58,46,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(31,58,46,0.06)'; }}
    >
      {/* Image */}
      <div style={{ position: 'relative', height: '210px', overflow: 'hidden', cursor: 'pointer' }} onClick={onDetail}>
        <img src={getImageUrl(room.images?.[0])} alt={room.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }} />
        {discount && (
          <div style={{
            position: 'absolute', top: '10px', right: '10px',
            background: 'linear-gradient(135deg, #dc2626, #f97316)', color: 'white',
            borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: 800,
            boxShadow: '0 4px 14px rgba(220,38,38,0.45)', letterSpacing: '0.02em',
            animation: 'flashPulse 2s infinite', maxWidth: '75%', textAlign: 'right',
          }} data-testid={`room-discount-badge-${room._id}`}>
            🔥 {discount.name} — %{discount.percent} {language === 'tr' ? 'İNDİRİM' : 'OFF'}
          </div>
        )}
        {/* Room name badge */}
        <div style={{ position: 'absolute', top: '10px', left: '10px' }}>
          <span style={{ background: 'rgba(31,58,46,0.9)', color: 'white', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px' }}>
            {room.name}
          </span>
        </div>
        {/* Price badge */}
        <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(255,255,255,0.96)', borderRadius: '8px', padding: '5px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {hasSearch && priceInfo ? (
            <div>
              <p style={{ fontSize: '11px', color: '#6E7F5B', marginBottom: '1px' }}>{nights} {language === 'tr' ? 'gece' : 'nights'}</p>
              <p style={{ fontSize: '15px', fontWeight: 800, color: '#1F3A2E' }}>{formatPrice(priceInfo.total_try)}</p>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#1F3A2E', display: 'inline' }}>{formatPrice(room.base_price)}</p>
              <span style={{ fontSize: '11px', color: '#6E7F5B' }}> {t('per_night')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 600, color: '#1F3A2E' }}>{room.name}</h3>
          <span style={{ fontSize: '12px', color: '#6E7F5B', flexShrink: 0, marginLeft: '8px' }}>
            <Users size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {room.capacity} · {room.size_m2}m²
          </span>
        </div>

        <p style={{ fontSize: '13px', color: '#5A5450', lineHeight: 1.6, marginBottom: '12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {language === 'tr' ? room.description_tr : room.description_en}
        </p>

        {/* Amenities */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '14px' }}>
          {(amenities || []).slice(0, 3).map((a, i) => (
            <span key={i} style={{ fontSize: '11px', background: '#EFE7DA', color: '#3E5A3F', padding: '3px 9px', borderRadius: '20px', fontWeight: 500 }}>{a}</span>
          ))}
          {(amenities || []).length > 3 && (
            <span style={{ fontSize: '11px', background: '#EFE7DA', color: '#6E7F5B', padding: '3px 9px', borderRadius: '20px' }}>+{(amenities || []).length - 3}</span>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            data-testid="room-card-view-details-button"
            onClick={onDetail}
            style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1.5px solid #D8D1C6', background: 'transparent', color: '#1F3A2E', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Manrope, sans-serif', transition: 'border-color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#1F3A2E'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#D8D1C6'}
          >
            {t('view_details')}
          </button>
          {hasSearch && (
            <button
              onClick={onBook}
              style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: '#1F3A2E', color: 'white', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Manrope, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#3E5A3F'}
              onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
            >
              {t('book_now')} <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
